import { FC, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { Buffer } from 'buffer';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';

import { isHusqvarna } from 'config';
import { authSelector } from 'store/authState';
import { productRegistrationSelector } from 'store/productRegistrationState/productRegistrationState';
import { signUp, fetchCaptcha, signUpSelector } from 'store/signupState';
import * as routes from 'router/routes';
import { useTranslations } from 'hooks/useTranslations';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { pushGTMEvent } from 'utils/analyticsUtils';
import { getCustomerSignupProductMeta } from 'utils/customerUtils';
import { showSuccessToast } from 'utils/toastUtils';
import { CustomerType, DiamGroups } from 'models/customer';
import userIcon from 'assets/vectors/user.svg';

import Loader from 'components/UI/Loader';
import Button from 'components/UI/Buttons/Button';
import InputField from 'components/UI/InputField';
import PhoneNumberField from 'components/UI/PhoneNumberField';
import BackButton from 'components/UI/Buttons/BackButton';
import PageHeader from 'components/UI/Page/PageHeader';
import PageImageWrapper from 'components/UI/Page/PageImageWrapper';
import SeoHelmet from 'components/fragments/SeoHelmet';

import { Form, ButtonContainer } from '../styled';
import { CaptchaContainer, CaptchaCode, TwoColGrid } from './styled';

export const CompanyMainContactPage: FC = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data, isSuccess, isLoading, isCompanyDetailsCompleted, error } =
    useAppSelector(signUpSelector);
  const { session, hasLoggedIn } = useAppSelector(authSelector);
  const { productInfo } = useAppSelector(productRegistrationSelector);

  // Define Zod schema
  const schema = z.object({
    firstName: z.string().min(1, t('signup.form.firstname_required')),
    lastName: z.string().min(1, t('signup.form.lastname_required')),
    dialCode: z.string().optional(),
    phoneNumber: z.string().optional(),
    captchaCode: z.string().min(1, t('signup.form.enter_captcha_prompt'))
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    setValue
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      firstName: data.firstName,
      lastName: data.lastName,
      dialCode: data.phoneNumber.dialCode,
      phoneNumber: data.phoneNumber.number,
      captchaCode: data.captchaCode
    },
    resolver: zodResolver(schema)
  });

  // State
  const { email, country, captchaElement } = data;
  const primaryContactEmail = session.user?.attributes.username ?? email;

  // Reset inputfield
  const reset = useCallback(
    (name: any) => {
      setValue(name, '');
      clearErrors(name);
    },
    [setValue, clearErrors]
  );

  // Fetch captcha
  useEffect(() => {
    dispatch(fetchCaptcha(primaryContactEmail));
  }, [dispatch, primaryContactEmail]);

  // Reset captcha code
  useEffect(() => {
    if (error) {
      dispatch(fetchCaptcha(primaryContactEmail));
    }
    if (error?.code === 'captcha.not.found') {
      reset('captchaCode');
    }
  }, [dispatch, reset, primaryContactEmail, error]);

  // Show success toast and handle navigation
  useEffect(() => {
    if (session.authenticated) {
      showSuccessToast(
        t(
          isSuccess
            ? 'signup.success.account_created'
            : 'signup.success.email_needs_verification'
        )
      );
    }
  }, [isSuccess, session.authenticated, t]);

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      pushGTMEvent('signupComplete', {
        customerType: CustomerType.OrganizationCustomer
      });
      const { dialCode, number } = data.phoneNumber;

      dispatch(
        signUp(
          {
            companyName: data.companyName,
            email: data.companyEmail,
            password: data.password,
            firstName: values.firstName,
            lastName: values.lastName,
            country: data.country,
            language: data.language,
            vatNumber: data.vatNumber,
            type: CustomerType.OrganizationCustomer,
            phoneNumber: dialCode && number ? `${dialCode}${number}` : '',
            locationAddress: data.address,
            billingAddress: data.address,
            shippingAddress: {},
            groups: [
              isHusqvarna ? DiamGroups.Husqvarna : DiamGroups.Construction
            ],
            primaryContact: {
              firstName: values.firstName,
              lastName: values.lastName,
              emailAddress: primaryContactEmail,
              phoneNumber:
                values.dialCode && values.phoneNumber
                  ? `${values.dialCode}${values.phoneNumber}`
                  : ''
            },
            captchaCode: values.captchaCode
          },
          {
            product: getCustomerSignupProductMeta(productInfo),
            consents: data.emailMarketing
              ? [
                  {
                    consentTypeId: data.consentType?.id ?? '',
                    status: 'ACCEPTED'
                  }
                ]
              : undefined
          }
        )
      );
    },
    [dispatch, productInfo, data, primaryContactEmail]
  );

  // Cancel company details
  const onBack = useCallback(() => {
    navigate(routes.SIGNUP_COMPANY);
  }, [navigate]);

  // On dial code change
  const onDialCodeChange = useCallback(
    (value: string) => setValue('dialCode', value),
    [setValue]
  );

  // Redirect to products if authenticated and account is created
  if (session.authenticated && isSuccess) {
    return <Navigate to={routes.PRODUCTS} />;
  }

  // Redirect to verify email if not authenticated and account is created
  if (!session.authenticated && isSuccess) {
    return (
      <Navigate
        to={`${routes.SIGNUP_VERIFY_EMAIL}?email=${encodeURIComponent(primaryContactEmail)}`}
      />
    );
  }

  // Redirect to login if auth is required and user is not authenticated
  if (!session.authenticated && hasLoggedIn) {
    return <Navigate to={routes.LOGIN} />;
  }

  // Redirect if previous state is not completed
  if (!isCompanyDetailsCompleted) {
    return <Navigate to={routes.SIGNUP_COMPANY} />;
  }

  if (isLoading) {
    return <Loader center padding size="large" />;
  }

  return (
    <PageImageWrapper alignTop>
      <SeoHelmet title="signup.company_main_contact.meta_title" />
      <BackButton onClick={onBack}>{t('productinfo.btn_back')}</BackButton>
      <PageHeader
        title={t('signup.company_main_contact.title')}
        subtitle={t('signup.company_main_contact.subtitle')}
        image={userIcon}
        imageAlt="user"
      />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <TwoColGrid>
          <InputField
            name="firstName"
            description="signup.form.label_firstname"
            error={errors.firstName}
            register={register('firstName')}
            reset={reset}
          />
          <InputField
            name="lastName"
            description="signup.form.label_lastname"
            error={errors.lastName}
            register={register('lastName')}
            reset={reset}
          />
        </TwoColGrid>
        <InputField
          name="email"
          description="signup.form.label_email"
          defaultValue={session.user?.attributes.username ?? email}
          disabled
        />
        <PhoneNumberField
          description="signup.form.label_phone_number"
          numberInputRef={register('phoneNumber')}
          dialCodeInputRef={register('dialCode')}
          onDialCodeChange={onDialCodeChange}
          defaultCountry={country}
          error={errors.phoneNumber}
        />
        {captchaElement && (
          <CaptchaContainer>
            <CaptchaCode
              src={`data:image/svg+xml;base64,${Buffer.from(
                captchaElement
              ).toString('base64')}`}
              alt="captcha"
            />
          </CaptchaContainer>
        )}
        <InputField
          name="captchaCode"
          description="signup.form.label_captcha_code"
          register={register('captchaCode')}
          error={errors.captchaCode}
          uppercase
        />
        <ButtonContainer>
          <Button submit fullWidth>
            {t('button.complete')}
          </Button>
          <Button
            onClick={onBack}
            color="background"
            textColor="primary"
            border
          >
            {t('button.back')}
          </Button>
        </ButtonContainer>
      </Form>
    </PageImageWrapper>
  );
};
